import { useEffect } from 'react';

import { useLazyQuery } from '@apollo/client';
import { useRouter } from 'next/router';

import { useAuth } from '@r-client/shared/data/auth';
import { useGqlClient } from '@r-client/shared/data/client';
import { useReporting } from '@r-client/shared/data/error-reporting';
import { getSessionStorage } from '@r-client/shared/util/core';
import {
  GetCryptoWalletsDocument,
  IGetCryptoWalletsQuery,
} from '@r-client/wallet/data/republic-sync';
import {
  useHasWalletQuery,
  useIsWarpDriveEnabled,
} from '@r-client/wallet/data/warp-drive';
import {
  getWarpNoWalletReportedStorageKey,
  WALLET_CREATION_ROUTES,
} from '@r-client/wallet/util/constants';

export const WARP_NO_WALLET_MESSAGE = 'Wallet#warpCustomerHasNoWallet';

const isWalletCreationPath = (pathname: string) =>
  WALLET_CREATION_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );

// Keys of the reports that session storage could not hold. Without it, a
// browser that blocks session storage would repeat the report on every route.
const reportedInMemory = new Set<string>();

const wasReported = (key: string) => {
  if (reportedInMemory.has(key)) return true;
  try {
    return getSessionStorage()?.getItem(key) != null;
  } catch {
    return false;
  }
};

const markReported = (key: string) => {
  try {
    const storage = getSessionStorage();
    if (storage) {
      storage.setItem(key, 'true');
      return;
    }
  } catch {
    // Fall through to the memory mark.
  }
  // Storage is missing or refuses the write: only the next page load reports again.
  reportedInMemory.add(key);
};

export interface IUseHasWalletOptions {
  /**
   * With `false`, the hook starts no read. A caller that already reads the
   * wallet from another source passes `false` for the case it does not need.
   */
  enabled?: boolean;
}

export const useHasWallet = ({ enabled = true }: IUseHasWalletOptions = {}) => {
  const { viewer } = useAuth();
  const reporting = useReporting();
  const { pathname } = useRouter();
  const isWarpDriveEnabled = useIsWarpDriveEnabled();
  const authId = viewer?.info?.authId;

  // Warp Drive path
  const hasWalletQuery = useHasWalletQuery({ lazy: true });

  // Legacy path
  const legacyClient = useGqlClient();
  const [
    getCryptoWallets,
    {
      data: cryptoWalletData,
      loading: cryptoWalletIsLoading,
      error: cryptoWalletQueryErrors,
    },
  ] = useLazyQuery<IGetCryptoWalletsQuery>(GetCryptoWalletsDocument, {
    client: legacyClient,
  });

  useEffect(() => {
    if (!enabled || !viewer?.isAuthenticated) return;

    // With Warp Drive on, the main-system read only feeds the no-wallet
    // report below. Both reads start in this one effect run, so the batch link
    // sends them in one request. The flag loads after the first render, and
    // that render already read the main system. The network-only policy sends
    // the read again with the Warp read, so the cache cannot separate the two.
    if (isWarpDriveEnabled) {
      hasWalletQuery.subscribe();
    }
    getCryptoWallets({
      variables: { investorProfileId: viewer?.info?.investorProfile?.id },
      ...(isWarpDriveEnabled && { fetchPolicy: 'network-only' }),
    });
  }, [
    enabled,
    viewer?.isAuthenticated,
    viewer?.info?.investorProfile?.id,
    isWarpDriveEnabled,
    hasWalletQuery,
    getCryptoWallets,
  ]);

  // Report a Warp customer whom the main system knows as a wallet owner, and
  // for whom Warp answered "no wallet". Only the republic wallet counts: an
  // external wallet never moves to Warp.
  const warpSettledWithNoWallet =
    isWarpDriveEnabled &&
    !hasWalletQuery.isLoading &&
    !hasWalletQuery.isRejected &&
    hasWalletQuery.data?.hasWallet === false;

  const mainSystemKnowsWallet =
    !cryptoWalletIsLoading &&
    !cryptoWalletQueryErrors &&
    !!cryptoWalletData?.cryptoWallets?.nodes.some(
      (wallet) => wallet.republicWallet
    );

  const shouldReportNoWallet = warpSettledWithNoWallet && mainSystemKnowsWallet;

  useEffect(() => {
    if (!shouldReportNoWallet || !authId) return;
    // The path is a dependency: a customer who leaves a creation route and
    // still has no wallet in Warp is reported on the next route.
    if (isWalletCreationPath(pathname)) return;

    // Several components of one page call this hook, so a ref would not stop
    // the duplicates. Session storage allows one report for each session.
    const storageKey = getWarpNoWalletReportedStorageKey(authId);
    if (wasReported(storageKey)) return;

    // A failing reporter must not break the header and the navigation that
    // call this hook. The session stays unmarked, so a later run tries again.
    try {
      reporting.info(WARP_NO_WALLET_MESSAGE);
    } catch {
      return;
    }
    markReported(storageKey);
  }, [shouldReportNoWallet, authId, reporting, pathname]);

  if (isWarpDriveEnabled) {
    const hasWallet =
      !hasWalletQuery.isRejected &&
      !hasWalletQuery.isLoading &&
      !!hasWalletQuery.data?.hasWallet;

    return {
      hasWallet,
      hasRepublicWallet: hasWallet,
      hasExternalWallet: false,
      cryptoWalletIsLoading: hasWalletQuery.isLoading,
      cryptoWalletHasError: hasWalletQuery.isRejected,
      // Settled = the query produced a definitive result (data or error), as
      // opposed to "not fired yet". Lets consumers distinguish "resolved, no
      // wallet" from "still loading" without relying on a loading=true tick.
      cryptoWalletIsSettled:
        (hasWalletQuery.data !== undefined && !hasWalletQuery.isLoading) ||
        hasWalletQuery.isRejected,
    };
  }

  const hasWallet =
    !cryptoWalletQueryErrors &&
    !cryptoWalletIsLoading &&
    !!cryptoWalletData?.cryptoWallets?.nodes.length;

  const hasRepublicWallet = !!cryptoWalletData?.cryptoWallets?.nodes.some(
    (wallet) => wallet.republicWallet
  );

  const hasExternalWallet = !!cryptoWalletData?.cryptoWallets?.nodes.some(
    (wallet) => !wallet.republicWallet
  );

  return {
    hasWallet,
    hasRepublicWallet,
    hasExternalWallet,
    cryptoWalletIsLoading,
    cryptoWalletHasError: !!cryptoWalletQueryErrors,
    cryptoWalletIsSettled:
      (cryptoWalletData !== undefined && !cryptoWalletIsLoading) ||
      !!cryptoWalletQueryErrors,
  };
};
